import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useCallback } from 'react'
import { isAxiosError } from 'axios'

import { useEducationHostedForm } from '../useEducationHostedForm/useEducationHostedForm'
import { useMutation } from '../useMutation/useMutation'

import { CAPTCHA_ACTIONS } from '@percent/utility'
import { useNavigateForm } from '@percent/education-hosted-form/app/EducationHostedForm/EducationHostedForm/hooks/useNavigateForm'
import { createValidationSubmission } from '@percent/education-hosted-form/api/validationSubmission/validationSubmission.actions'
import {
  EducationHostedFormModel,
  EducationHostedFormSteps
} from '@percent/education-hosted-form/app/EducationHostedForm/EducationHostedForm/EducationHostedForm.types'
import {
  CreateValidationSubmissionProps,
  ValidationSubmissionStatus
} from '@percent/education-hosted-form/api/validationSubmission/validationSubmission.types'
import { FormState } from '@percent/education-hosted-form/context/educationHostedForm/EducationHostedFormContext.types'
import { ErrorBase } from '@percent/education-hosted-form/errors/ErrorBase'
import { APIErrorResponse } from '@percent/education-hosted-form/types/api.types'
import { isInvalidCaptchaTokenError } from '@percent/education-hosted-form/utils/isInvalidCaptchaTokenError'

const DEFAULT_LANGUAGE = 'en-US'
const OTP_JWT_INVALID_CODE = 'validation_submission/otp_jwt_invalid'

export const useHandleValidationSubmission = () => {
  const { getValues, setValue } = useFormContext<EducationHostedFormModel>()
  const {
    partnerConfig,
    setFormState,
    clearOtpCooldown,
    setMustReverifyOtp,
    setMustReverifyCaptcha,
    agentVerificationId
  } = useEducationHostedForm()
  const { programId } = useParams<{ programId: string }>()
  const { handleNextStep } = useNavigateForm()

  const { mutate: mutateValidationSubmission, isPending: isCreatingVS } = useMutation(createValidationSubmission, {
    captchaAction: CAPTCHA_ACTIONS.validationSubmission,
    onError: (error: ErrorBase) => {
      const errorCode = isAxiosError<APIErrorResponse>(error.cause)
        ? error.cause.response?.data?.error?.code
        : undefined

      if (errorCode === OTP_JWT_INVALID_CODE) {
        // JWT expired or invalid — clear it, reset OTP cooldown so the OTP step auto-sends
        // a fresh code on remount, surface a banner explaining why the user is back, and
        // bounce them to verify again.
        setValue('otpJwt', undefined)
        setMustReverifyCaptcha(false)
        clearOtpCooldown()
        setMustReverifyOtp(true)
        handleNextStep(EducationHostedFormSteps.OTP)

        return
      }

      if (isInvalidCaptchaTokenError(error)) {
        // The challenge was blocked, timed out, or the token was spent. Keep the user
        // here with a notice. FormState.ERROR would lose the form.
        setMustReverifyCaptcha(true)

        return
      }

      setFormState(FormState.ERROR)
    },
    onSuccess: ({ data }) => {
      // All three terminal screens are wired, but for the GoDaddy MVP only
      // PENDING_APPROVE is reachable — BE returns PENDING for every submission.
      // Approved / Rejected become live with AV-2511 (instant-verification
      // re-introduction).
      switch (data.status) {
        case ValidationSubmissionStatus.SUCCEEDED:
          handleNextStep(EducationHostedFormSteps.APPROVED)
          break
        case ValidationSubmissionStatus.PENDING:
        case ValidationSubmissionStatus.PROCESSED:
          handleNextStep(EducationHostedFormSteps.PENDING_APPROVE)
          break
        case ValidationSubmissionStatus.FAILED:
          handleNextStep(EducationHostedFormSteps.REJECTED)
          break
      }
    }
  })

  const handleCreateValidationSubmission = useCallback(() => {
    if (programId && partnerConfig?.configuration.id) {
      const formValues = getValues()

      const addOrganisationModel = {
        organisationId: undefined,
        organisationName: formValues.organisationName,
        countryCode: formValues.country,
        website: formValues.organisationWebsite || undefined,
        addressLine1: formValues.organisationAddressLine1,
        addressLine2: formValues.organisationAddressLine2 || undefined,
        city: formValues.organisationCity,
        state: formValues.organisationState,
        postal: formValues.organisationPostalCode
      }

      const formModel: CreateValidationSubmissionProps = {
        validationInviteId: programId,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        organisationId: formValues.organisationId,
        language: DEFAULT_LANGUAGE,
        otpJwt: getValues('otpJwt'),
        externalAgentVerificationId: agentVerificationId,
        ...(!formValues.organisationId ? addOrganisationModel : {})
      }

      // Clear the old captcha notice before a retry.
      setMustReverifyCaptcha(false)
      mutateValidationSubmission(formModel)
    }
  }, [
    agentVerificationId,
    getValues,
    mutateValidationSubmission,
    partnerConfig?.configuration.id,
    programId,
    setMustReverifyCaptcha
  ])

  return { handleCreateValidationSubmission, isCreatingVS }
}
